/**
 * FindingChip — the one component that renders a finding's outcome × evidence
 * basis (Results v2 §2 + §3, build step 1).
 *
 * The encoding, honestly stated:
 *  - fill presence = answered — structural (§3.1): every open treatment is
 *    unfilled or washed, never tinted as an outcome;
 *  - within answered: texture = basis (solid measured / 45° hatch attested),
 *    colour + label = outcome (§3.2);
 *  - the three open treatments are visibly distinct (§3.3): teal-dashed
 *    "a person must answer", neutral-dashed "nothing could answer", and
 *    hard-solid "our collector failed" — never one grey;
 *  - outcome is never conveyed by colour alone (§3.4): the label is part of
 *    the chip, and the chip announces all three axes to a screen reader.
 *
 * An impossible matrix combination throws in dev and renders the safe open
 * treatment in prod (resolveFindingState). The disputed overlay (§3.5) stays
 * at row level — DC-1799 shipped it; this chip does not restate it.
 *
 * ARC IP boundary: identifiers and outcome labels only.
 */

import React from 'react';
import { CONFIDENCE_LABEL, OUTCOME_VOCABULARY } from './assessmentTypes';
import { resolveFindingState, type MatrixFields } from './findingState';

export const FindingChip: React.FC<{ finding: MatrixFields }> = ({ finding }) => {
  const state = resolveFindingState(finding);
  const label =
    OUTCOME_VOCABULARY[finding.outcome]?.label ?? finding.outcome.replace(/_/g, ' ');
  const basisLabel = CONFIDENCE_LABEL[finding.confidence] ?? finding.confidence;

  const className =
    state.kind === 'answered'
      ? `uc-chip uc-chip-answered uc-chip-tone-${state.tone} uc-chip-basis-${state.basis}`
      : `uc-chip uc-chip-open uc-chip-${state.treatment}`;

  return (
    <span
      role="img"
      aria-label={`${finding.testCode}, ${label}, ${basisLabel}`}
      className={className}
      data-testid={`chip-${finding.testCode}`}
      data-degraded={state.kind === 'open' && state.degraded ? 'true' : undefined}
    >
      <span className="uc-chip-label">{label}</span>
    </span>
  );
};

export default FindingChip;
